import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode
} from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  startIcon?: ReactNode
  endIcon?: ReactNode
}

export function Button({
  children,
  className = '',
  variant = 'primary',
  size = 'md',
  loading = false,
  startIcon,
  endIcon,
  disabled,
  ...props
}: ButtonProps) {
  const variants: Record<ButtonVariant, string> = {
    primary: 'border-hc-brand bg-hc-brand text-white hover:border-hc-brand-hover hover:bg-hc-brand-hover active:border-hc-brand-pressed active:bg-hc-brand-pressed',
    secondary: 'border-hc-border bg-hc-surface text-hc-text-secondary hover:border-hc-border-strong hover:bg-hc-main active:bg-hc-subtle',
    tertiary: 'border-hc-secondary bg-hc-secondary text-white hover:border-hc-secondary-hover hover:bg-hc-secondary-hover active:border-hc-secondary-pressed active:bg-hc-secondary-pressed',
    danger: 'border-hc-danger bg-hc-danger text-white hover:border-hc-danger-hover hover:bg-hc-danger-hover active:border-hc-danger-pressed active:bg-hc-danger-pressed',
    ghost: 'border-transparent bg-transparent text-hc-text-secondary hover:bg-hc-subtle active:bg-hc-border'
  }
  const sizes: Record<ButtonSize, string> = {
    sm: 'min-h-8 px-3 text-[13px]',
    md: 'min-h-9 px-3.5 text-sm',
    lg: 'min-h-10 px-4 text-sm'
  }

  return (
    <button
      aria-busy={loading || undefined}
      className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border font-bold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hc-brand disabled:cursor-not-allowed disabled:border-hc-border disabled:bg-hc-subtle disabled:text-hc-disabled motion-reduce:transition-none ${variants[variant]} ${sizes[size]} ${className}`.trim()}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <span className="size-4 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none" aria-hidden="true" /> : startIcon}
      <span>{children}</span>
      {!loading ? endIcon : null}
    </button>
  )
}

export function Card({className = '', ...props}: HTMLAttributes<HTMLElement>) {
  return <article className={`rounded-xl border border-hc-border bg-hc-surface shadow-sm ${className}`.trim()} {...props} />
}

type BadgeTone = 'neutral' | 'positive' | 'info' | 'warning' | 'danger' | 'negotiating' | 'agreement'

export function Badge({children, tone = 'neutral'}: {children: ReactNode; tone?: BadgeTone}) {
  const tones: Record<BadgeTone, string> = {
    neutral: 'bg-hc-subtle text-hc-muted',
    positive: 'bg-emerald-50 text-hc-paid',
    info: 'bg-blue-50 text-hc-due',
    warning: 'bg-amber-50 text-hc-partial',
    danger: 'bg-red-50 text-hc-disputed',
    negotiating: 'bg-teal-50 text-hc-negotiating',
    agreement: 'bg-blue-100 text-hc-agreement'
  }
  return <span className={`inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide ${tones[tone]}`}>{children}</span>
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string
  hint?: string
  error?: string
  success?: string
}

export function Field({className = '', error, hint, id, label, required, success, ...props}: FieldProps) {
  const helper = error ?? success ?? hint
  const helperId = id && helper ? `${id}-helper` : undefined
  const state = error
    ? 'border-hc-danger focus-visible:border-hc-danger focus-visible:outline-hc-danger/20'
    : success
      ? 'border-hc-paid focus-visible:border-hc-paid focus-visible:outline-hc-paid/20'
      : 'border-hc-border hover:border-hc-border-strong focus-visible:border-hc-brand focus-visible:outline-hc-brand/20'

  return (
    <label className="grid gap-1.5 text-[13px] font-bold text-hc-text">
      <span>{label}{required ? <span className="ml-0.5 text-hc-danger" aria-hidden="true">*</span> : null}</span>
      <input
        aria-describedby={helperId}
        aria-invalid={error ? true : undefined}
        className={`min-h-9 rounded-md border bg-hc-surface px-3 text-sm font-normal text-hc-text outline-none placeholder:text-hc-placeholder focus-visible:border-[1.5px] focus-visible:outline-3 disabled:cursor-not-allowed disabled:bg-hc-subtle disabled:text-hc-disabled read-only:bg-hc-subtle ${state} ${className}`.trim()}
        id={id}
        required={required}
        {...props}
      />
      {helper ? <small id={helperId} className={`text-xs font-normal ${error ? 'text-hc-danger' : success ? 'text-hc-paid' : 'text-hc-text-secondary'}`}>{helper}</small> : null}
    </label>
  )
}
